import {
	dxtToLocalServerTime,
	getMomentTz,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import ListIcon from "@mui/icons-material/List";
import { Button, Grid2 as Grid, Tooltip } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	FERN,
	FROSTED_MINT,
	MAGNOLIA,
	MANGO_TANGO,
	PURPLE_HEART,
	SAN_MARINO,
} from "../../../../../controls/shared/constants/colors";
import { useGridStyles } from "../../../styles/gridStyles";
import { useProductionOptionsFunctions } from "./useProductionOptionsFunctions";

type Props = {
	onDeclareClick: (processId: number, ladleClose?: Date | null) => void;
};
const moment = getMomentTz();

// Number formatter for US locale (commas for thousands, period for decimals)
const numberFormatter = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

export const useTableData = ({ onDeclareClick }: Props) => {
	const classes = useGridStyles();
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { getMenuOptions, goToCasterDataEntry, goToHeatReport } =
		useProductionOptionsFunctions({ onDeclareClick });

	useEffect(() => {
		registerConfig({
			id: "production-management",
			getOptions: getMenuOptions,
		});
	}, []);

	const columnData: ColDef[] = [
		{
			headerName: "Heat",
			field: "HeatName",
			flex: 1,
			minWidth: 64,
		},
		{
			headerName: "Grade",
			field: "GradeName",
			flex: 1,
			minWidth: 78,
		},
		{
			headerName: "Mold Size",
			field: "MoldSizeName",
			flex: 1,
			minWidth: 84,
		},
		{
			headerName: "Foreman",
			field: "CasterForeman",
			flex: 1,
			minWidth: 77,
		},
		{
			headerName: "Production Date",
			field: "CastDate",
			flex: 1,
			minWidth: 123,
			valueGetter: (params) =>
				params.data.CastDate
					? moment(params.data.CastDate).format("MM/DD/YYYY")
					: "",
		},
		{
			headerName: "Tap Time",
			field: "TapTime",
			flex: 1,
			minWidth: 80,
			valueGetter: (params) =>
				params.data.TapTime ? moment(params.data.TapTime).format("HH:mm") : "",
		},
		{
			headerName: "Melter",
			field: "Melter",
			flex: 1,
			minWidth: 63,
		},
		{
			headerName: "Lab Tech",
			field: "LabTech",
			flex: 1,
			minWidth: 80,
		},
		{
			headerName: "Sequence",
			field: "Sequence",
			flex: 1,
			minWidth: 84,
		},
		{
			headerName: "Total Length",
			field: "TotalLength",
			flex: 1,
			minWidth: 120,
			wrapHeaderText: true,
			headerStyle: {
				textAlign: "center",
			},
			valueGetter: (params) => {
				if (!params.data.TotalLength || params.data.TotalLength === 0)
					return "";
				const totalInches = params.data.TotalLength;
				const feet = Math.floor(totalInches / 12);
				const inches = totalInches % 12;
				return `${numberFormatter.format(feet)} ft ${inches} in`;
			},
		},
		{
			headerName: "Total Pieces",
			field: "TotalPieces",
			flex: 1,
			wrapHeaderText: true,
			headerStyle: {
				textAlign: "center",
			},
			minWidth: 66,
			valueGetter: (params) => {
				if (!params.data.TotalPieces || params.data.TotalPieces === 0)
					return "";
				return numberFormatter.format(params.data.TotalPieces);
			},
		},
		{
			headerName: "Section",
			field: "Sections",
			flex: 1,
			minWidth: 90,
			wrapHeaderText: true,
			headerStyle: {
				textAlign: "center",
			},
			valueGetter: (params) => {
				return params.data.Sections || "";
			},
		},
		{
			headerName: "Total Weight [lbs]",
			field: "TotalWeight",
			flex: 1,
			wrapHeaderText: true,
			headerStyle: {
				textAlign: "center",
			},
			minWidth: 100,
			valueGetter: (params) => {
				if (!params.data.TotalWeight || params.data.TotalWeight === 0)
					return "";
				return numberFormatter.format(params.data.TotalWeight);
			},
		},
		{
			headerName: "Pourback [tons]",
			field: "PourbackWeight",
			flex: 1,
			wrapHeaderText: true,
			headerStyle: {
				textAlign: "center",
			},
			minWidth: 110,
			valueGetter: (params) => {
				if (!params.data.PourbackWeight || params.data.PourbackWeight === 0)
					return "";
				return numberFormatter.format(params.data.PourbackWeight);
			},
		},
		// Status Column
		{
			headerName: "Status",
			field: "Declared",
			flex: 1,
			minWidth: 130,
			valueGetter: (params) => {
				// Priority 1: Pourback (if any pourback exists)
				if (params.data.HasPourback) {
					return "Pourback";
				}
				// Priority 2: Declared or Pending
				return params.data.Declared ? "Declared" : "Pending";
			},
			cellStyle: (params) => {
				// Pourback - Violet with background
				if (params.data.HasPourback) {
					return {
						color: PURPLE_HEART,
						fontWeight: "bold",
						backgroundColor: MAGNOLIA,
					};
				}
				// Declared - Green with background
				if (params.data.Declared) {
					return {
						color: FERN,
						fontWeight: "bold",
						backgroundColor: FROSTED_MINT,
					};
				}
				// Pending - Orange, no background
				return {
					color: MANGO_TANGO,
					fontWeight: "bold",
					backgroundColor: "",
				};
			},
		},
		// Declare/Re-declare Button
		{
			headerName: "",
			field: "Actions",
			flex: 1,
			minWidth: 120,
			maxWidth: 120,
			sortable: false,
			cellRenderer: (params: any) => {
				const isDeclared = Boolean(params.data.Declared);
				const hasPourback = Boolean(params.data.HasPourback);

				// Determine button properties
				let buttonText = "Declare";
				let buttonVariant: "contained" | "outlined" = "contained";
				let tooltipText = "Declare product batch";

				if (isDeclared || hasPourback) {
					buttonText = "View";
					buttonVariant = "outlined";
					tooltipText = "Product batch already declared";
				}

				return (
					<div className={classes.alwaysVisible}>
						<Tooltip title={tooltipText} arrow>
							<Button
								fullWidth
								variant={buttonVariant}
								color="primary"
								size="small"
								onClick={() =>
									onDeclareClick(params.data.ProcessId, params.data.LadleClose)
								}
								style={{
									fontSize: "0.75rem",
									padding: "3px 8px",
									minWidth: "93px",
								}}
							>
								{buttonText}
							</Button>
						</Tooltip>
					</div>
				);
			},
		},
		// Links and Options Column
		{
			headerName: "",
			flex: 1,
			minWidth: 180,
			maxWidth: 180,
			field: "links",
			sortable: false,
			cellRenderer: (params: any) => {
				return (
					<div className={classes.buttons}>
						<Grid container justifyContent="center" alignItems="center">
							<Grid size={{ xs: 4, md: 4 }} style={{ padding: "0" }}>
								<Tooltip title="See in Caster Data Entry" arrow>
									<Button
										fullWidth
										aria-label="Caster Data Entry"
										onClick={() => {
											goToCasterDataEntry(params.data.ProcessId);
										}}
									>
										<span
											className="material-icons"
											style={{ color: SAN_MARINO }}
										>
											memory
										</span>
									</Button>
								</Tooltip>
							</Grid>
							<Grid size={{ xs: 4, md: 4 }} style={{ padding: "0" }}>
								<Tooltip title="See Heat Report" arrow>
									<Button
										fullWidth
										aria-label="Heat Report"
										onClick={() => {
											goToHeatReport(params.data.HeatId);
										}}
									>
										<span
											className="material-icons"
											style={{ color: SAN_MARINO }}
										>
											description
										</span>
									</Button>
								</Tooltip>
							</Grid>
							<Grid size={{ xs: 4, md: 4 }} style={{ padding: "0" }}>
								<Button
									fullWidth
									aria-label="Options"
									title="Options"
									style={{ cursor: "pointer" }}
									size="large"
									onClick={(e) =>
										showContextMenu(e, params.data, "production-management")
									}
								>
									<ListIcon
										fontSize="inherit"
										color="primary"
										style={{ cursor: "pointer", height: "20px", width: "20px" }}
									/>
								</Button>
							</Grid>
						</Grid>
					</div>
				);
			},
		},
	];

	return { columnData, showContextMenu };
};
